import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast, useDisclosure } from "@chakra-ui/react";
import debounce from "lodash.debounce";
import { FiPlus, FiSend, FiUser, FiUserPlus } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";
import SkeletonBase from "newui/components/Skeleton";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { useMeetingAttendees } from "../../hooks/useMeetingAttendees";
import { useMeeting } from "../../hooks/useMeeting";
import type { Meeting, CreateMeetingRequest } from "../../types/meeting.type";
import { searchCompanyUsers } from "newui/features/Rfx/Evaluation/api/panel-api";
import type { CompanyUser } from "newui/features/Rfx/Evaluation/types/panel.type";
import { useCurrentCompany } from "hooks/auth";
import { AttendeeRow } from "./AttendeeRow";
import { MeetingInviteDrawer } from "./MeetingInviteDrawer";

type AddMode = "company" | "external";

interface MeetingInviteViewProps {
  evalId: number;
  meeting: Meeting;
  rfxId: number;
  rfxLeadName: string;
  rfxTitle: string;
  onBack: () => void;
}

interface AddAttendeeFormState {
  name: string;
  email: string;
}

export const MeetingInviteView: React.FC<MeetingInviteViewProps> = ({
  rfxId,
  evalId,
  meeting,
  rfxLeadName,
  rfxTitle,
  onBack,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>("company");
  const [addForm, setAddForm] = useState<AddAttendeeFormState>({ name: "", email: "" });

  // Company user search state
  const { id: companyId } = useCurrentCompany();
  const [companyUsers, setCompanyUsers] = useState<SelectOption[]>([]);
  const [companyUserEmailMap, setCompanyUserEmailMap] = useState<Map<number, string>>(new Map());
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedCompanyUser, setSelectedCompanyUser] = useState<SelectOption | null>(null);

  const { attendees, isLoading: isAttendeesLoading, addAttendee } = useMeetingAttendees(rfxId, evalId);
  const { updateMeeting } = useMeeting(rfxId, evalId);
  const [resendIds, setResendIds] = useState<Set<number>>(new Set());

  const hasSchedule = !!meeting.scheduled_at && !!meeting.timezone;

  // Split attendees into groups
  const { teamAttendees, externalAttendees, unsentAttendees, sentAttendees } = useMemo(() => {
    const team = attendees.filter((a) => a.is_panel_member);
    const external = attendees.filter((a) => !a.is_panel_member);
    const unsent = attendees.filter((a) => !a.invitation_sent_at);
    const sent = attendees.filter((a) => !!a.invitation_sent_at);
    return { teamAttendees: team, externalAttendees: external, unsentAttendees: unsent, sentAttendees: sent };
  }, [attendees]);

  const handleResendToggle = (id: number, checked: boolean) => {
    setResendIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const hasNewInvitees = unsentAttendees.length > 0;
  const hasResendSelection = resendIds.size > 0;

  // Already-added emails to filter from dropdown
  const addedEmails = useMemo(
    () => new Set(attendees.map((a) => a.email.toLowerCase())),
    [attendees]
  );

  const availableCompanyUsers = useMemo(
    () => companyUsers.filter((u) => {
      const email = companyUserEmailMap.get(u.value as number);
      return !email || !addedEmails.has(email.toLowerCase());
    }),
    [companyUsers, companyUserEmailMap, addedEmails]
  );

  const updateCompanyUsers = (users: CompanyUser[]): void => {
    const options = users.map((u) => ({
      label: `${u.full_name} (${u.email})`,
      value: u.id,
    }));
    const emailMap = new Map<number, string>();
    users.forEach((u) => {
      emailMap.set(u.id, u.email.toLowerCase());
    });
    setCompanyUsers(options);
    setCompanyUserEmailMap(emailMap);
  };

  // Load company users when form opens
  useEffect(() => {
    if (!showAddForm || !companyId) return;
    setIsSearchingUsers(true);
    searchCompanyUsers(companyId, "")
      .then((users) => updateCompanyUsers(users))
      .catch(() => setCompanyUsers([]))
      .finally(() => setIsSearchingUsers(false));
  }, [showAddForm, companyId]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!companyId) {
        setCompanyUsers([]);
        setIsSearchingUsers(false);
        return;
      }
      try {
        const users = await searchCompanyUsers(companyId, query);
        updateCompanyUsers(users);
      } catch {
        setCompanyUsers([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300),
    [companyId]
  );

  const handleCompanyUserSearch = (inputValue: string, actionMeta: { action: string }) => {
    if (actionMeta.action !== "input-change") return;
    setIsSearchingUsers(true);
    debouncedSearch(inputValue);
  };

  const handleAddCompanyUser = async () => {
    if (!selectedCompanyUser) return;
    try {
      await addAttendee.mutateAsync({ user_id: selectedCompanyUser.value as number });
      setSelectedCompanyUser(null);
      toast({
        title: "Attendee added",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to add attendee. They may already be on the list.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleAddExternalAttendee = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      return;
    }
    try {
      await addAttendee.mutateAsync({ name: addForm.name.trim(), email: addForm.email.trim() });
      setAddForm({ name: "", email: "" });
      toast({
        title: "Attendee added",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to add attendee. They may already be on the list.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setAddForm({ name: "", email: "" });
    setSelectedCompanyUser(null);
  };

  const handleSendInvitations = () => {
    // Always open the drawer — it handles both setting schedule details and sending
    onDrawerOpen();
  };

  const handleSaveMeetingAndSend = async (payload: CreateMeetingRequest) => {
    try {
      const { sendMeetingInvites } = await import("../../api/meeting-api");

      // Build attendee IDs to send: unsent attendees + any selected for resend
      const idsToSend = [
        ...unsentAttendees.map((a) => a.id),
        ...Array.from(resendIds),
      ];

      await sendMeetingInvites(rfxId, evalId, {
        scheduled_at: payload.scheduled_at,
        timezone: payload.timezone,
        location: payload.location,
        message: payload.message,
        attendee_ids: idsToSend.length > 0 ? idsToSend : undefined,
      });

      // Invalidate both meeting and attendees to refresh UI
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["meeting", rfxId, evalId] }),
        queryClient.invalidateQueries({ queryKey: ["meetingAttendees", rfxId, evalId] }),
      ]);

      setResendIds(new Set());

      toast({
        title: "Invitations sent",
        description: "Meeting invitations have been sent to all attendees.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      onBack();
    } catch {
      toast({
        title: "Error",
        description: "Failed to send invitations. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleSaveMeeting = async (payload: CreateMeetingRequest) => {
    await updateMeeting.mutateAsync(payload);
  };

  if (isAttendeesLoading) {
    return (
      <div className="space-y-3" data-testid="meeting-invite-view-loading">
        <SkeletonBase startColor="lightgray" width="100%" height="60px" className="!rounded-lg" />
        <SkeletonBase startColor="lightgray" width="100%" height="60px" className="!rounded-lg" />
        <SkeletonBase startColor="lightgray" width="100%" height="60px" className="!rounded-lg" />
      </div>
    );
  }

  return (
    <div data-testid="meeting-invite-view" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-gray-900">Meeting Invitations</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage attendees and send meeting invitations
          </p>
        </div>
        <Button btntype="secondary" onClick={onBack} data-testid="back-btn">
          Back
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-700">
            Attendees ({attendees.length})
          </p>
          <Button
            btntype="secondary"
            onClick={() => setShowAddForm((prev) => !prev)}
            data-testid="add-attendee-toggle-btn"
          >
            <FiPlus className="h-4 w-4 mr-1" />
            Add Attendee
          </Button>
        </div>

        {showAddForm && (
          <div
            data-testid="add-attendee-form"
            className="px-4 py-3 bg-gray-50 border-b border-gray-100"
          >
            {/* Mode tabs */}
            <div className="flex gap-1 mb-3">
              <button
                type="button"
                onClick={() => setAddMode("company")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${addMode === "company"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-white text-gray-500 hover:bg-gray-100 border border-blue-300"
                  }`}
                data-testid="add-mode-company"
              >
                <FiUser className="h-3.5 w-3.5" />
                Team Member
              </button>
              <button
                type="button"
                onClick={() => setAddMode("external")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${addMode === "external"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-white text-gray-500 hover:bg-gray-100 border border-blue-300"
                  }`}
                data-testid="add-mode-external"
              >
                <FiUserPlus className="h-3.5 w-3.5" />
                External
              </button>
            </div>

            {addMode === "company" ? (
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <SelectWithLabel
                    label="Search team members"
                    placeholder="Search by name or email..."
                    name="company-user-select"
                    className="!mb-0"
                    options={availableCompanyUsers}
                    value={selectedCompanyUser}
                    onChange={(option) => setSelectedCompanyUser(option as SelectOption | null)}
                    onInputChange={handleCompanyUserSearch}
                    isLoading={isSearchingUsers}
                    isClearable
                    data-testid="company-user-select"
                    clearAble
                  />
                </div>
                <div className="flex gap-2 pb-1">
                  <Button
                    btntype="primary"
                    onClick={handleAddCompanyUser}
                    disabled={!selectedCompanyUser || addAttendee.isPending}
                    isLoading={addAttendee.isPending}
                    data-testid="add-attendee-submit-btn"
                  >
                    Add
                  </Button>
                  <Button
                    btntype="secondary"
                    onClick={handleCloseAddForm}
                    data-testid="add-attendee-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <CompactTextInput
                    value={addForm.name}
                    onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Full name"
                    data-testid="add-attendee-name"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <CompactTextInput
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                    data-testid="add-attendee-email"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    btntype="primary"
                    onClick={handleAddExternalAttendee}
                    disabled={!addForm.name.trim() || !addForm.email.trim() || addAttendee.isPending}
                    isLoading={addAttendee.isPending}
                    data-testid="add-attendee-submit-btn"
                  >
                    Add
                  </Button>
                  <Button
                    btntype="secondary"
                    onClick={handleCloseAddForm}
                    data-testid="add-attendee-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-4">
          {attendees.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400" data-testid="no-attendees-msg">
              No attendees yet. Add panel members or ad-hoc attendees above.
            </p>
          ) : (
            <>
              {/* Team / Panel members */}
              {teamAttendees.length > 0 && (
                <div data-testid="team-attendees-section">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide pt-3 pb-1">
                    Team Members
                  </p>
                  {teamAttendees.map((attendee) => (
                    <AttendeeRow
                      key={attendee.id}
                      attendee={attendee}
                      selectable={!!attendee.invitation_sent_at}
                      selected={resendIds.has(attendee.id)}
                      onSelectChange={handleResendToggle}
                    />
                  ))}
                </div>
              )}
              {/* External attendees */}
              {externalAttendees.length > 0 && (
                <div data-testid="external-attendees-section">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide pt-3 pb-1">
                    External
                  </p>
                  {externalAttendees.map((attendee) => (
                    <AttendeeRow
                      key={attendee.id}
                      attendee={attendee}
                      selectable={!!attendee.invitation_sent_at}
                      selected={resendIds.has(attendee.id)}
                      onSelectChange={handleResendToggle}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {sentAttendees.length > 0 && resendIds.size > 0 && (
        <p className="text-xs text-gray-500">
          {resendIds.size} attendee{resendIds.size !== 1 ? "s" : ""} selected to resend invitations
        </p>
      )}

      <div className="flex justify-end gap-2">
        {hasSchedule && (
          <Button
            btntype="secondary"
            onClick={onDrawerOpen}
            data-testid="edit-meeting-details-btn"
          >
            Edit Details
          </Button>
        )}
        <Button
          btntype="primary"
          onClick={handleSendInvitations}
          disabled={
            attendees.length === 0 ||
            meeting.status === "in_progress" ||
            meeting.status === "completed" ||
            (!hasNewInvitees && !hasResendSelection)
          }
          data-testid="send-invites-btn"
        >
          <FiSend className="h-4 w-4 mr-2" />
          {hasSchedule ? "Send Invitations" : "Schedule & Send Invitations"}
        </Button>
      </div>

      <MeetingInviteDrawer
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        attendeeCount={attendees.length}
        existingMeeting={meeting}
        onSave={handleSaveMeeting}
        onSend={handleSaveMeetingAndSend}
        rfxLeadName={rfxLeadName}
        rfxTitle={rfxTitle} />
    </div>
  );
};
